import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { NAV_LINKS, MORE_LINKS, SCHOOL, LOGO } from "@/lib/constants";
import { Button } from "@/components/ui/button";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (p) => location.pathname === p;

  return (
    <>
      {/* Top bar */}
      <div className="hidden md:flex items-center justify-between bg-navy text-slate-200 text-xs px-6 py-2">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-gold" /> {SCHOOL.landline}</span>
          <span className="text-slate-400">|</span>
          <span>{SCHOOL.phones.join(" / ")}</span>
        </div>
        <div className="flex items-center gap-4">
          <span>Office: {SCHOOL.officeTiming}</span>
          <Link to="/contact" className="text-gold hover:underline" data-testid="topbar-contact-link">Contact Us</Link>
          <Link to="/admin/login" className="text-slate-400 hover:text-white" data-testid="topbar-admin-link">Admin</Link>
        </div>
      </div>

      <header
        className={`sticky top-0 z-40 transition-colors ${
          scrolled ? "glass border-b border-slate-200 shadow-sm" : "bg-white"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-[74px]">
            <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
              <img src={LOGO} alt="B.R.Smart Academy logo" className="h-12 w-12 object-contain" />
              <div className="leading-tight">
                <div className="font-head font-extrabold text-navy text-lg sm:text-xl">B.R.Smart Academy</div>
                <div className="text-[10px] sm:text-xs text-brandred font-semibold tracking-wide">ESTD 2018 · CBSE English Medium</div>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-1">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.path}
                  to={l.path}
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`px-3 py-2 rounded-md text-sm font-semibold transition-colors ${
                    isActive(l.path) ? "text-brandred" : "text-navy hover:text-brandred"
                  }`}
                >
                  {l.label}
                </Link>
              ))}
              <div className="relative" onMouseEnter={() => setMoreOpen(true)} onMouseLeave={() => setMoreOpen(false)}>
                <button
                  data-testid="nav-more"
                  className="px-3 py-2 rounded-md text-sm font-semibold text-navy hover:text-brandred flex items-center gap-1"
                >
                  More <ChevronDown className="h-4 w-4" />
                </button>
                {moreOpen && (
                  <div className="absolute right-0 top-full w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-2">
                    {MORE_LINKS.map((l) => (
                      <Link
                        key={l.path}
                        to={l.path}
                        data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                        className="block px-4 py-2 text-sm font-medium text-navy hover:bg-slate-50 hover:text-brandred"
                      >
                        {l.label}
                      </Link>
                    ))}
                    <Link to="/contact" className="block px-4 py-2 text-sm font-medium text-navy hover:bg-slate-50 hover:text-brandred">Contact</Link>
                  </div>
                )}
              </div>
              <Link to="/admission" data-testid="header-apply-btn">
                <Button className="ml-2 bg-brandred hover:bg-brandred/90 text-white rounded-full px-5 font-semibold">
                  Apply Now
                </Button>
              </Link>
            </nav>

            <button
              className="lg:hidden text-navy"
              onClick={() => setOpen((o) => !o)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-slate-100 bg-white px-4 py-3 space-y-1 max-h-[70vh] overflow-y-auto">
            {[...NAV_LINKS, ...MORE_LINKS, { label: "Contact", path: "/contact" }].map((l) => (
              <Link
                key={l.path}
                to={l.path}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`block px-3 py-2.5 rounded-lg font-semibold ${
                  isActive(l.path) ? "bg-brandred/10 text-brandred" : "text-navy hover:bg-slate-50"
                }`}
              >
                {l.label}
              </Link>
            ))}
            <Link to="/admission" data-testid="mobile-apply-btn">
              <Button className="w-full mt-2 bg-brandred hover:bg-brandred/90 text-white rounded-full font-semibold">
                Apply Now
              </Button>
            </Link>
            <Link to="/admin/login" className="block px-3 py-2 text-xs text-slate-400">Admin Login</Link>
          </div>
        )}
      </header>
    </>
  );
};
